import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { apiRequest } from '../helpers/api';

export interface Tag {
  _id: string;
  name: string;
  description?: string;
  posts: string[];
}

export const fetchAllTags = createAsyncThunk(
  'tags/fetchAllTags',
  async (teamId: string) => {
    return await apiRequest<Tag[]>('GET', `/tags/microsoft/${teamId}`);
  },
);

export const fetchTagById = createAsyncThunk(
  'tags/fetchTagById',
  async (id: string) => {
    return await apiRequest<Tag>('GET', `/tags/${id}`);
  },
);

export const createNewTag = createAsyncThunk(
  'tags/createNewTag',
  async (payload: { microsoftTeamId: string; name: string }) => {
    return await apiRequest<Tag>('POST', '/tags', undefined, payload);
  },
);

export const fetchTagsByPostId = createAsyncThunk(
  'tags/fetchTagsByPostId',
  async (postId: string) => {
    return await apiRequest<Tag[]>('GET', `/posts/${postId}/tags`);
  },
);

interface AddPostToTagsDto {
  tagsIds: string[];
  postId: string;
}

export const addPostToTags = createAsyncThunk(
  'tags/addPostToTags',
  async (addPostToTagsDto: AddPostToTagsDto) => {
    const result = await apiRequest<Tag[]>('POST', '/tags/posts', undefined, {
      tagsIds: addPostToTagsDto.tagsIds,
      postId: addPostToTagsDto.postId,
    });

    return result.map((tag) => {
      return {
        id: tag._id,
        changes: { posts: tag.posts },
      };
    });
  },
);

export const tagsAdapter = createEntityAdapter<Tag>({
  selectId: (tag: Tag) => tag._id,
});

const tagSlice = createSlice({
  name: 'tags',
  initialState: tagsAdapter.getInitialState({ isLoading: false }),
  reducers: {
    removeAllTags: tagsAdapter.removeAll,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllTags.fulfilled, (state, action) => {
        state.isLoading = false;
        tagsAdapter.setAll(state, action);
      })
      .addCase(fetchAllTags.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllTags.rejected, (state) => {
        state.isLoading = false;
      });
    builder.addCase(createNewTag.fulfilled, tagsAdapter.upsertOne);
    builder.addCase(fetchTagById.fulfilled, tagsAdapter.setOne);
    builder.addCase(fetchTagsByPostId.fulfilled, tagsAdapter.setAll);
    builder.addCase(addPostToTags.fulfilled, tagsAdapter.updateMany);
  },
});

export const { removeAllTags } = tagSlice.actions;

export default tagSlice.reducer;
